/**
 * Monotonic clock for visit-recording event timestamps.
 *
 * Why monotonic instead of Date.now():
 * - Phones lie. Wall clock can jump backwards (NTP correction, manual reset).
 * - The video timeline is built from these offsets — ordering and video sync
 *   would break if the clock skewed mid-visit.
 *
 * Implementation:
 * - On RN, performance.now() returns a monotonic ms count from process start.
 * - We anchor at startSession() and report (now - sessionStart).
 * - If performance is somehow unavailable, fall back to Date.now() but log
 *   a Sentry warning so we know to look into it.
 */

let sessionStartMonotonicMs: number | null = null;

function nowMs(): number {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
        return performance.now();
    }
    return Date.now();
}

export const MonotonicClock = {
    startSession(): void {
        sessionStartMonotonicMs = nowMs();
    },

    /** ms offset from session start. 0 if session hasn't been started. */
    offsetMs(): number {
        if (sessionStartMonotonicMs === null) {
            return 0;
        }
        return Math.max(0, Math.round(nowMs() - sessionStartMonotonicMs));
    },

    reset(): void {
        sessionStartMonotonicMs = null;
    },

    /** Visible only for tests. */
    _state(): { started: boolean } {
        return { started: sessionStartMonotonicMs !== null };
    }
};
